import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type {
  SiteContract, SiteContractListResponse, SiteContractListParams,
  SiteContractInput, SetInvestorRateInput, InvestorRateEmbed, MathPreview,
} from '@/lib/types';

export async function fetchContracts(params: SiteContractListParams): Promise<SiteContractListResponse> {
  const { data } = await apiClient.get<SiteContractListResponse>('/api/v1/site-contracts', { params });
  return data;
}

export async function fetchContract(id: string): Promise<SiteContract> {
  const { data } = await apiClient.get<{ contract: SiteContract }>(`/api/v1/site-contracts/${id}`);
  return data.contract;
}

export async function createContract(input: SiteContractInput): Promise<SiteContract> {
  const { data } = await apiClient.post<{ contract: SiteContract }>('/api/v1/site-contracts', input);
  return data.contract;
}

export async function updateContract(id: string, input: Partial<SiteContractInput>): Promise<SiteContract> {
  const { data } = await apiClient.patch<{ contract: SiteContract }>(`/api/v1/site-contracts/${id}`, input);
  return data.contract;
}

export async function setInvestorRate(
  contractId: string,
  input: SetInvestorRateInput
): Promise<InvestorRateEmbed> {
  const { data } = await apiClient.post<{ investorRate: InvestorRateEmbed }>(
    `/api/v1/site-contracts/${contractId}/investor-rates`,
    input
  );
  return data.investorRate;
}

export async function listInvestorRates(contractId: string): Promise<InvestorRateEmbed[]> {
  const { data } = await apiClient.get<{ items: InvestorRateEmbed[] }>(
    `/api/v1/site-contracts/${contractId}/investor-rates`
  );
  return data.items;
}

export async function previewMath(
  contractId: string,
  deliveredQty: number,
  investorRateAtTarget: number
): Promise<MathPreview> {
  const { data } = await apiClient.get<{ preview: MathPreview }>(
    `/api/v1/site-contracts/${contractId}/preview-math`,
    { params: { deliveredQty, investorRateAtTarget } }
  );
  return data.preview;
}

export function useContracts(params: SiteContractListParams) {
  return useQuery({
    queryKey: ['contracts', params],
    queryFn: () => fetchContracts(params),
    placeholderData: (prev) => prev,
  });
}

export function useContract(id: string | null | undefined) {
  return useQuery({
    queryKey: ['contract', id],
    queryFn: () => fetchContract(id!),
    enabled: !!id,
  });
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createContract,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      qc.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}

export function useUpdateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<SiteContractInput> }) =>
      updateContract(id, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      qc.setQueryData(['contract', updated.id], updated);
    },
  });
}

export function useSetInvestorRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contractId, input }: { contractId: string; input: SetInvestorRateInput }) =>
      setInvestorRate(contractId, input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['contract', vars.contractId] });
      qc.invalidateQueries({ queryKey: ['contracts'] });
    },
  });
}
